import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fetchCentrosCostos } from './services/centroCostos.services';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const fmt = (v) =>
  v != null && !isNaN(v)
    ? `$${Number(v).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

const getMonthBounds = (periodo) => {
  const [year, month] = periodo.split('-').map(Number);
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  const last = new Date(year, month, 0).getDate();
  const lastStr = `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { first, last: lastStr };
};

const nivelColor = {
  empresa: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  centro: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  cliente: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

const ResumenMesModal = ({ isOpen, onClose, periodo, periodoData }) => {
  const [tab, setTab] = useState('ventas');
  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(false);

  useEffect(() => {
    if (!isOpen || !periodo) return;
    setTab('ventas');
    setGastos([]);
    const { first, last } = getMonthBounds(periodo);
    setLoadingGastos(true);
    fetchCentrosCostos({ limit: 200, offset: 1, query: { fechaDesde: first, fechaHasta: last } })
      .then(res => setGastos(res.data || []))
      .catch(() => setGastos([]))
      .finally(() => setLoadingGastos(false));
  }, [isOpen, periodo]);

  const [añoStr, mesStr] = periodo ? periodo.split('-') : ['', ''];
  const mesLabel = MESES[parseInt(mesStr) - 1] || '';

  // Ventas: group by center
  const clientes = periodoData?.clientes || [];
  const totalVentas = periodoData?.totalVentas || 0;
  const grupos = clientes.reduce((acc, c) => {
    const key = c.centroId || 'sin-centro';
    if (!acc[key]) acc[key] = { centroNombre: c.centroNombre || 'Sin centro', clientes: [] };
    acc[key].clientes.push(c);
    return acc;
  }, {});

  // Gastos summary
  const totalGastos = gastos.reduce((s, g) => s + (g.costoGeneral || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Resumen — {mesLabel} {añoStr}</DialogTitle>
        </DialogHeader>

        {/* Summary bar */}
        <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="bg-blue-500/10 rounded-lg px-3 py-2">
            <p className="text-muted-foreground">Total ventas</p>
            <p className="font-semibold text-sm">{fmt(totalVentas)}</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg px-3 py-2">
            <p className="text-muted-foreground">Clientes con ventas</p>
            <p className="font-semibold text-sm">{clientes.filter(c => c.ventas > 0).length} / {clientes.length}</p>
          </div>
          <div className="bg-emerald-500/10 rounded-lg px-3 py-2">
            <p className="text-muted-foreground">Total gastos</p>
            <p className="font-semibold text-sm">{fmt(totalGastos)}</p>
          </div>
          <div className="bg-emerald-500/10 rounded-lg px-3 py-2">
            <p className="text-muted-foreground">Registros de gastos</p>
            <p className="font-semibold text-sm">{gastos.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex border-b border-border gap-0">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'ventas' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab('ventas')}
          >
            Ventas por cliente
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'gastos' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab('gastos')}
          >
            Gastos registrados
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">

          {/* ── Tab: Ventas ── */}
          {tab === 'ventas' && (
            clientes.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                No hay ventas registradas para este período.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Cliente</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Ventas</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">% General</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">% Centro</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grupos).map(([centroId, grupo]) => {
                    const totalCentro = grupo.clientes.reduce((s, c) => s + c.ventas, 0);
                    return (
                      <>
                        <tr key={`hdr-${centroId}`} className="border-t-2 border-border bg-blue-500/8">
                          <td colSpan={4} className="px-3 py-1.5">
                            <span className="font-semibold text-blue-700 dark:text-blue-300">{grupo.centroNombre}</span>
                            <span className="ml-2 text-muted-foreground font-normal">
                              {grupo.clientes.length} clientes · {fmt(totalCentro)}
                              {totalVentas > 0 && <span className="ml-1 text-blue-600 dark:text-blue-400">({(totalCentro / totalVentas * 100).toFixed(1)}%)</span>}
                            </span>
                          </td>
                        </tr>
                        {grupo.clientes.map(c => (
                          <tr key={c.id} className="border-t border-border/60 hover:bg-muted/20">
                            <td className="px-3 py-1.5 pl-7">{c.nombre}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmt(c.ventas)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                              {c.participacionGeneral ? c.participacionGeneral.toFixed(2) : '0.00'}%
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-blue-600 dark:text-blue-400">
                              {c.participacionPorCentro ? c.participacionPorCentro.toFixed(2) : '0.00'}%
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-border">
                  <tr className="bg-muted/20 font-semibold">
                    <td className="px-3 py-2">Total — {clientes.length} clientes</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(totalVentas)}</td>
                    <td className="px-3 py-2 text-right">100%</td>
                    <td className="px-3 py-2 text-right text-muted-foreground font-normal text-xs">por centro</td>
                  </tr>
                </tfoot>
              </table>
            )
          )}

          {/* ── Tab: Gastos ── */}
          {tab === 'gastos' && (
            loadingGastos ? (
              <div className="flex items-center justify-center gap-2 h-32 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando gastos...
              </div>
            ) : gastos.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                No hay gastos registrados para este período.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Fecha</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Descripción</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Categoría</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Nivel</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground"># Clientes</th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground">Costo Total</th>
                  </tr>
                </thead>
                <tbody>
                  {gastos.map(g => (
                    <tr key={g._id} className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {g.fecha ? new Date(g.fecha).toLocaleDateString('es-CO') : '—'}
                      </td>
                      <td className="px-3 py-1.5 max-w-[180px] truncate" title={g.descripcion}>{g.descripcion}</td>
                      <td className="px-3 py-1.5">
                        <span className="px-2 py-0.5 rounded-full bg-muted">{g.categoria}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${nivelColor[g.nivel] || ''}`}>
                          {g.nivel}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right">{g.numeroClientes ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(g.costoGeneral)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-border">
                  <tr className="bg-muted/20 font-semibold">
                    <td className="px-3 py-2" colSpan={5}>Total — {gastos.length} registros</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(totalGastos)}</td>
                  </tr>
                </tfoot>
              </table>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResumenMesModal;
